import { useEffect, useState } from "react";
import { loadYouTubeApi } from "../../../../utils/youtube-iframe-api";

const UNAVAILABLE_ERROR_CODES = new Set([100, 101, 150]);
const CUED_STATE = 5;
const META_TIMEOUT_MS = 8000;

const IDLE_META = { status: "idle", duration: null };
const LOADING_META = { status: "loading", duration: null };

/** Settled results by video id, so reopening the panel or switching back does not ask YouTube again. */
const metaCache = new Map();

/**
 * Duration and availability of a YouTube video, read from a hidden player.
 *
 * There is no backend lookup: the IFrame API reports the duration once the video is cued
 * and fires error 100/101/150 for removed, private or non-embeddable videos. Anything we
 * cannot tell (API blocked, timeout) ends as `unknown`, never as `unavailable`.
 *
 * The hidden player is destroyed as soon as it has answered. Nothing loads until `enabled`, so a closed panel costs no YouTube request. `ok` and
 * `unavailable` results are cached per video id for the life of the page; `unknown` is not,
 * so a blocked API or a timeout is retried next time.
 *
 * @param {string|null} videoId
 * @param {boolean} enabled
 * @returns {{ status: "idle" | "loading" | "ok" | "unavailable" | "unknown", duration: number|null }}
 */
export default function useYouTubeVideoMeta(videoId, enabled) {
  const [meta, setMeta] = useState(IDLE_META);

  /**
   * Effect to fetch the YouTube video metadata.
   * @param {string|null} videoId
   * @param {boolean} enabled
   * @returns {void}
   */
  useEffect(() => {
    if (!videoId || !enabled) {
      setMeta(IDLE_META);
      return undefined;
    }

    const cached = metaCache.get(videoId);
    if (cached) {
      setMeta(cached);
      return undefined;
    }

    setMeta(LOADING_META);
    let cancelled = false;
    let player = null;
    let wrapper = null;
    let timeoutId = null;
    let settled = false;

    const destroyPlayer = () => {
      try {
        player?.destroy?.();
      } catch (error) {
        console.error("[useYouTubeVideoMeta] could not destroy player", error);
      }
      player = null;
      wrapper?.remove();
      wrapper = null;
    };
    const finish = (next) => {
      if (cancelled || settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      if (next.status !== "unknown") {
        metaCache.set(videoId, next);
      }
      setMeta(next);
      setTimeout(destroyPlayer, 0);
    };
    const readDuration = (target) => {
      const duration = target.getDuration?.();
      return duration > 0 ? duration : null;
    };

    timeoutId = setTimeout(
      () => finish({ status: "unknown", duration: null }),
      META_TIMEOUT_MS
    );

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled) {
          return;
        }
        wrapper = document.createElement("div");
        wrapper.setAttribute("aria-hidden", "true");
        wrapper.style.cssText =
          "position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none";
        const host = document.createElement("div");
        wrapper.appendChild(host);
        document.body.appendChild(wrapper);

        player = new YT.Player(host, {
          width: "1",
          height: "1",
          videoId,
          playerVars: { controls: 0, playsinline: 1 },
          events: {
            onReady: (event) => {
              const duration = readDuration(event.target);
              if (duration) {
                finish({ status: "ok", duration });
              }
            },
            onStateChange: (event) => {
              if (event.data === CUED_STATE) {
                finish({
                  status: "ok",
                  duration: readDuration(event.target),
                });
              }
            },
            onError: (event) => {
              if (UNAVAILABLE_ERROR_CODES.has(event.data)) {
                finish({ status: "unavailable", duration: null });
              }
            },
          },
        });
      })
      .catch(() => finish({ status: "unknown", duration: null }));

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      destroyPlayer();
    };
  }, [videoId, enabled]);

  return meta;
}
